import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from '../routing';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/AuthShell';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const redirectRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectRef.current), []);

  React.useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t('resetPassword.passwordMismatch')); return; }
    // 8 is the backend's rule. At 6 the form let the request through and the
    // server answered with an express-validator body ({ errors: [...] }, no
    // `error`), so the user only ever saw the generic "invalid link" fallback.
    if (password.length < 8) { toast.error(t('resetPassword.tooShort')); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      // Kept so it can be cleared: navigating away from an unmounted component
      // three seconds later yanks whatever screen the user has since opened.
      redirectRef.current = setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err?.error || err?.errors?.[0]?.msg || t('resetPassword.invalidToken'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>

        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 32 }}>
            <Sparkles size={18} color={theme.gold} />
            <span style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink }}>GLAM<span style={{ color: theme.gold }}>GO</span></span>
          </Link>
          <h1 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, margin: '0 0 8px', fontWeight: 400 }}>{t('resetPassword.title')}</h1>
          <p style={{ color: theme.grey400, fontSize: 14, margin: 0 }}>{t('resetPassword.subtitle')}</p>
        </div>

        {!token ? (
          <div style={{ textAlign: 'center' }}>
            <AlertCircle size={48} color={theme.red} style={{ marginBottom: 16 }} />
            <p style={{ color: theme.grey600 }}>{t('resetPassword.invalidLink')}</p>
            <Link to="/forgot-password" style={{ color: theme.gold, fontSize: 14 }}>{t('resetPassword.requestNew')}</Link>
          </div>
        ) : done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} color={theme.green} style={{ marginBottom: 16 }} />
            <h3 style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.ink, marginBottom: 8 }}>{t('resetPassword.successTitle')}</h3>
            <p style={{ color: theme.grey600, fontSize: 14 }}>{t('resetPassword.successMessage')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {[
              { label: t('resetPassword.newPassword'), val: password, set: setPassword },
              { label: t('resetPassword.confirmPassword'), val: confirm, set: setConfirm },
            ].map(({ label, val, set }) => (
              <div key={label} style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{label}</label>
                <input
                  type="password" value={val} onChange={e => set(e.target.value)} required minLength={8}
                  style={{ width: '100%', padding: '14px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 15, color: theme.ink, background: theme.cream, outline: 'none', boxSizing: 'border-box', fontFamily: theme.font.body }}
                  onFocus={e => e.target.style.borderColor = theme.gold}
                  onBlur={e => e.target.style.borderColor = theme.sand}
                />
              </div>
            ))}
            <button type="submit" disabled={loading} style={{ width: '100%', padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.15em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: theme.font.body, fontWeight: 500 }}>
              {loading ? t('resetPassword.saving') : t('resetPassword.save')}
            </button>
          </form>
        )}
    </AuthShell>
  );
}
